import { useEffect, useState, type FormEvent } from "react";
import {
  GPT_IMAGE_2_DOCUMENTED_SIZES,
  isDocumentedImageSize,
} from "@tandem/image-studio-shared";
import { api, ApiClientError } from "../api.js";
import { useAuth } from "../auth.js";
import { aspectRatio, safeAreaLabel } from "../ui.js";
import type { PlatformCategory, PlatformPreset } from "../types.js";

const empty: Omit<
  PlatformPreset,
  | "id"
  | "slug"
  | "aspectRatio"
  | "status"
  | "createdAt"
  | "updatedAt"
  | "safeAreaUnit"
> = {
  name: "",
  category: "custom",
  width: 1024,
  height: 1024,
  description: "",
  safeArea: { top: 0, right: 0, bottom: 0, left: 0 },
  compositionGuidance: "",
  outputFormatDefault: "png",
  sortOrder: 0,
};
export function PlatformsScreen() {
  const auth = useAuth();
  const [items, setItems] = useState<PlatformPreset[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("active");
  const [editing, setEditing] = useState<PlatformPreset | null | "new">(null);
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const result = await api.listPlatformPresets(
        new URLSearchParams({
          search,
          category,
          status,
          pageSize: "100",
          sort: "sort_order",
          order: "asc",
        }),
      );
      setItems(result.items);
      setTotal(result.total);
      setError("");
    } catch {
      setError("Не удалось загрузить площадки.");
    }
  };
  useEffect(() => {
    void load();
  }, [search, category, status]);
  return (
    <section className="panel release-table-panel">
      <div className="page-heading">
        <div>
          <h1>Площадки</h1>
          <p>Форматы и размеры изображений · {total} записей</p>
        </div>
        {auth.user?.role === "admin" ? (
          <button
            className="primary compact-button"
            onClick={() => setEditing("new")}
          >
            ＋ Новая площадка
          </button>
        ) : null}
      </div>
      <div className="table-toolbar template-toolbar">
        <label className="search-field">
          ⌕
          <input
            aria-label="Поиск площадок"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <select
          aria-label="Категория площадки"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          <option value="all">Все категории</option>
          {platformCategories.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Статус площадки"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="active">Активные</option>
          <option value="archived">Архивные</option>
          <option value="all">Все</option>
        </select>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      <div className="release-table platform-table">
        <div className="release-row release-head">
          <span>Площадка</span>
          <span>Категория</span>
          <span>Размер</span>
          <span>Соотношение</span>
          <span>Безопасная зона</span>
          <span>Формат</span>
          <span>Статус</span>
          <span />
        </div>
        {items.map((preset) => (
          <div className="release-row" key={preset.id}>
            <span>
              <strong>{preset.name}</strong>
            </span>
            <span>
              {platformCategories.find(
                ([value]) => value === preset.category,
              )?.[1] ?? preset.category}
            </span>
            <span>
              {preset.width}×{preset.height}
            </span>
            <span>{aspectRatio(preset)}</span>
            <span>{safeAreaLabel(preset)}</span>
            <span>{preset.outputFormatDefault.toUpperCase()}</span>
            <span>
              <b className={`status-pill ${preset.status}`}>
                {preset.status === "active" ? "Активна" : "В архиве"}
              </b>
            </span>
            <span className="row-actions">
              {auth.user?.role === "admin" ? (
                <button
                  aria-label={`Изменить ${preset.name}`}
                  onClick={() => setEditing(preset)}
                >
                  ✎
                </button>
              ) : null}
            </span>
          </div>
        ))}
      </div>
      {editing ? (
        <PlatformEditor
          preset={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

function PlatformEditor({
  preset,
  onClose,
  onSaved,
}: {
  preset: PlatformPreset | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    ...empty,
    ...(preset ?? {}),
    safeArea: { ...(preset?.safeArea ?? empty.safeArea) },
  });
  const [error, setError] = useState("");
  const updateSafe = (key: keyof PlatformPreset["safeArea"], value: number) =>
    setForm((current) => ({
      ...current,
      safeArea: { ...current.safeArea, [key]: value },
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (!isDocumentedImageSize(form.width, form.height)) {
      setError(
        "Выберите один из размеров в списке. Для новых генераций доступны варианты из документации OpenAI.",
      );
      return;
    }
    try {
      const payload = {
        name: form.name,
        category: form.category,
        width: Number(form.width),
        height: Number(form.height),
        description: form.description,
        safeArea: form.safeArea,
        compositionGuidance: form.compositionGuidance,
        outputFormatDefault: form.outputFormatDefault,
        sortOrder: Number(form.sortOrder),
      };
      if (preset) await api.updatePlatformPreset(preset.id, payload);
      else await api.createPlatformPreset(payload);
      onSaved();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError ? reason.message : "Ошибка сохранения",
      );
    }
  };
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog platform-editor"
        aria-label={preset ? "Редактировать площадку" : "Новая площадка"}
        onSubmit={(event) => void submit(event)}
      >
        <div className="dialog-heading">
          <div>
            <h2>{preset ? preset.name : "Новая площадка"}</h2>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="platform-editor-grid">
          <div>
            <label>
              Название
              <input
                aria-label="Название площадки"
                required
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
              />
            </label>
            <div className="form-grid">
              <label>
                Категория
                <select
                  value={form.category}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      category: event.target.value as PlatformCategory,
                    })
                  }
                >
                  {platformCategories.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Формат
                <select
                  value={form.outputFormatDefault}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      outputFormatDefault: event.target.value as
                        "png" | "jpeg" | "webp",
                    })
                  }
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </label>
            </div>
            <div className="form-grid">
              <label>
                Размер генерации
                <select
                  aria-label="Размер генерации"
                  value={`${form.width}x${form.height}`}
                  onChange={(event) => {
                    const size = GPT_IMAGE_2_DOCUMENTED_SIZES.find(
                      (item) =>
                        `${item.width}x${item.height}` === event.target.value,
                    );
                    if (size)
                      setForm({
                        ...form,
                        width: size.width,
                        height: size.height,
                      });
                  }}
                >
                  {!isDocumentedImageSize(form.width, form.height) && (
                    <option value={`${form.width}x${form.height}`} disabled>
                      Устаревший размер — выберите новый
                    </option>
                  )}
                  {GPT_IMAGE_2_DOCUMENTED_SIZES.map((size) => (
                    <option
                      key={size.label}
                      value={`${size.width}x${size.height}`}
                    >
                      {size.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <small className="control-hint">
              Размеры из документации OpenAI для GPT Image 2. Оригинал
              сохраняется без изменения размера, обрезки и полей. 4K может
              требовать больше времени.
            </small>
            <label>
              Описание
              <textarea
                rows={3}
                value={form.description}
                onChange={(event) =>
                  setForm({ ...form, description: event.target.value })
                }
              />
            </label>
            <label>
              Рекомендации по композиции
              <textarea
                aria-label="Composition guidance"
                rows={4}
                value={form.compositionGuidance}
                onChange={(event) =>
                  setForm({ ...form, compositionGuidance: event.target.value })
                }
              />
            </label>
          </div>
          <div>
            <div className="ratio-preview-wrap">
              <div
                className="ratio-preview"
                style={{
                  aspectRatio: `${Math.max(form.width, 1)} / ${Math.max(form.height, 1)}`,
                }}
              >
                <span>безопасная зона</span>
              </div>
              <strong>{ratio(form.width, form.height)}</strong>
              <small>
                {form.width} × {form.height}px
              </small>
            </div>
            <fieldset className="safe-grid">
              <legend>Безопасная зона · проценты</legend>
              {(["top", "right", "bottom", "left"] as const).map((key) => (
                <label key={key}>
                  {
                    {
                      top: "Сверху",
                      right: "Справа",
                      bottom: "Снизу",
                      left: "Слева",
                    }[key]
                  }
                  <input
                    aria-label={`Safe ${key}`}
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    value={form.safeArea[key]}
                    onChange={(event) =>
                      updateSafe(key, Number(event.target.value))
                    }
                  />
                </label>
              ))}
            </fieldset>
            <label>
              Порядок сортировки
              <input
                type="number"
                value={form.sortOrder}
                onChange={(event) =>
                  setForm({ ...form, sortOrder: Number(event.target.value) })
                }
              />
            </label>
          </div>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
          {preset?.status === "active" ? (
            <button
              type="button"
              className="danger-button"
              onClick={() =>
                void api.archivePlatformPreset(preset.id).then(onSaved)
              }
            >
              Архивировать
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
const platformCategories: Array<[PlatformCategory, string]> = [
  ["website", "Сайт"],
  ["social", "Соцсети"],
  ["advertising", "Реклама"],
  ["ecommerce", "Интернет-магазин"],
  ["custom", "Пользовательская"],
];
function ratio(width: number, height: number) {
  const divisor = gcd(width, height);
  return `${width / divisor}:${height / divisor}`;
}
function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a || 1;
}
